/**
 * Date and Time formatting utility for consistent display across the application
 * Supports multiple date and time formats that can be configured in user settings
 */

// Available date format options
export const DATE_FORMATS = {
  'DD/MM/YYYY': { label: 'DD/MM/YYYY (31/12/2026)', example: '31/12/2026' },
  'MM/DD/YYYY': { label: 'MM/DD/YYYY (12/31/2026)', example: '12/31/2026' },
  'YYYY-MM-DD': { label: 'YYYY-MM-DD (2026-12-31)', example: '2026-12-31' },
  'DD-MM-YYYY': { label: 'DD-MM-YYYY (31-12-2026)', example: '31-12-2026' },
  'DD MMM YYYY': { label: 'DD MMM YYYY (31 Dec 2026)', example: '31 Dec 2026' },
};

// Available time format options
export const TIME_FORMATS = {
  '24h': { label: '24-hour (14:30)', example: '14:30' },
  '12h': { label: '12-hour (2:30 PM)', example: '2:30 PM' },
};

/**
 * Format a date string or Date object according to the specified format
 * @param {string|Date} dateInput - The date to format (ISO string, date string, or Date object)
 * @param {string} format - The format to use (from DATE_FORMATS)
 * @returns {string} - Formatted date string
 */
export function formatDate(dateInput, format = 'DD/MM/YYYY') {
  if (!dateInput) return '';
  
  let date;
  if (typeof dateInput === 'string') {
    // Handle ISO strings and date-only strings
    date = new Date(dateInput.includes('T') ? dateInput : dateInput + 'T00:00:00');
  } else {
    date = dateInput;
  }
  
  if (isNaN(date.getTime())) return dateInput; // Return original if invalid
  
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const pad = (n) => n.toString().padStart(2, '0');
  
  switch (format) {
    case 'DD/MM/YYYY':
      return `${pad(day)}/${pad(month)}/${year}`;
    case 'MM/DD/YYYY':
      return `${pad(month)}/${pad(day)}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${pad(month)}-${pad(day)}`;
    case 'DD-MM-YYYY':
      return `${pad(day)}-${pad(month)}-${year}`;
    case 'DD MMM YYYY':
      return `${pad(day)} ${monthNames[month - 1]} ${year}`;
    default:
      return `${pad(day)}/${pad(month)}/${year}`;
  }
}

/**
 * Format a date for display with short month format
 * @param {string|Date} dateInput - The date to format
 * @param {string} format - The format preference
 * @returns {string} - Formatted date with short month
 */
export function formatDateShort(dateInput, format = 'DD/MM/YYYY') {
  if (!dateInput) return '';
  
  let date;
  if (typeof dateInput === 'string') {
    date = new Date(dateInput.includes('T') ? dateInput : dateInput + 'T00:00:00');
  } else {
    date = dateInput;
  }
  
  if (isNaN(date.getTime())) return dateInput;
  
  const day = date.getDate();
  const month = date.getMonth();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // For short format, always use "D Mon" style
  return `${day} ${monthNames[month]}`;
}

/**
 * Get date format string for HTML date inputs (always YYYY-MM-DD)
 * HTML date inputs require ISO format regardless of display preference
 */
export function toInputDate(dateInput) {
  if (!dateInput) return '';
  
  let date;
  if (typeof dateInput === 'string') {
    date = new Date(dateInput.includes('T') ? dateInput : dateInput + 'T00:00:00');
  } else {
    date = dateInput;
  }
  
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Format a time string according to the specified format
 * @param {string} timeInput - Time in HH:MM format (24h)
 * @param {string} format - '24h' or '12h'
 * @returns {string} - Formatted time string
 */
export function formatTime(timeInput, format = '24h') {
  if (!timeInput) return '';
  
  // Handle HH:MM format
  const [hours, minutes] = timeInput.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeInput;
  
  if (format === '12h') {
    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
  
  // 24h format
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Format a datetime string with both date and time
 * @param {string|Date} dateTimeInput - ISO datetime string or Date object
 * @param {string} dateFormat - Date format from DATE_FORMATS
 * @param {string} timeFormat - Time format ('24h' or '12h')
 * @returns {string} - Formatted datetime string
 */
export function formatDateTime(dateTimeInput, dateFormat = 'DD/MM/YYYY', timeFormat = '24h') {
  if (!dateTimeInput) return '';
  
  let date;
  if (typeof dateTimeInput === 'string') {
    date = new Date(dateTimeInput);
  } else {
    date = dateTimeInput;
  }
  
  if (isNaN(date.getTime())) return dateTimeInput;
  
  const dateStr = formatDate(date, dateFormat);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const timeStr = formatTime(`${hours}:${minutes}`, timeFormat);
  
  return `${dateStr} ${timeStr}`;
}
